import { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ChevronDown,
  ChevronUp,
  X,
  Calendar,
  CheckCircle2,
  Clock3,
  Clipboard,
  MessageSquare,
  Save,
  Search,
  Maximize,
  Minimize,
  UploadCloud,
  FileText,
  FileImage,
  File,
  Trash2,
  Download
} from "lucide-react";
import { getRequest, postRequest } from "../../services/apiService";

export default function UpdateTaskSubtaskForm({ shipId, equipmentId, onClose }) {
  const [activeHeader, setActiveHeader] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [formData, setFormData] = useState({
    status: "In Progress",
    percentage: 0,
    startDate: "",
    endDate: "",
    remarks: "",
    transactionId: null
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [isTaskAccordionOpen, setIsTaskAccordionOpen] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [headers, setHeaders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskUnits, setTaskUnits] = useState({});
  const [unitLabels, setUnitLabels] = useState({});
  const [existingTransactions, setExistingTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEndOfHierarchy, setIsEndOfHierarchy] = useState(false);
  const [taskDetail, setTaskDetail] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);

  const headerColors = [
    { bg: 'bg-gradient-to-r from-purple-500 to-purple-600', border: 'border-purple-300', shadow: 'shadow-purple-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-blue-500 to-blue-600', border: 'border-blue-300', shadow: 'shadow-blue-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-green-500 to-green-600', border: 'border-green-300', shadow: 'shadow-green-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-orange-500 to-orange-600', border: 'border-orange-300', shadow: 'shadow-orange-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-red-500 to-red-600', border: 'border-red-300', shadow: 'shadow-red-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-teal-500 to-teal-600', border: 'border-teal-300', shadow: 'shadow-teal-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-indigo-500 to-indigo-600', border: 'border-indigo-300', shadow: 'shadow-indigo-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-pink-500 to-pink-600', border: 'border-pink-300', shadow: 'shadow-pink-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-cyan-500 to-cyan-600', border: 'border-cyan-300', shadow: 'shadow-cyan-200', text: 'text-white' },
    { bg: 'bg-gradient-to-r from-yellow-500 to-yellow-600', border: 'border-yellow-300', shadow: 'shadow-yellow-200', text: 'text-gray-800' }
  ];

  const getHeaderColor = (index) => {
    return headerColors[index % headerColors.length];
  };

  useEffect(() => {
    const fetchHeaders = async () => {
      try {
        const response = await getRequest(`/pert/task-transaction-api/?parent_task_id=null&project_id=${shipId}`);
        setHeaders(response.data);
      } catch (error) {
        console.error('Error fetching headers:', error);
      }
    };
    fetchHeaders();
  }, [shipId]);

  useEffect(() => {
    const fetchTasksAndTransactions = async () => {
      if (activeHeader?.id) {
        try {
          const [tasksRes, transactionsRes] = await Promise.all([
            getRequest(`/pert/task-transaction-api/?parent_task_id=${activeHeader.id}&project_id=${shipId}`),
            getRequest(`/transaction/task-transaction?project_id=${shipId}&task__parent_task=${activeHeader.id}`)
          ]);
          setTasks(tasksRes.data);
          setExistingTransactions(transactionsRes.data);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
    };
    fetchTasksAndTransactions();
  }, [activeHeader, shipId]);

  const getUnitConfig = (headerName) => {
    switch (headerName) {
      case 'Hull Structure':
        return {
          endpoint: (taskId) => `/pert/task-transaction-api/?parent_task_id=${taskId}&project_id=${shipId}`,
          label: 'Units',
          resourceType: 'mpr_unit',
          field: 'mpr_unit'
        };
      case 'Hull Outfit':
        return {
          endpoint: (taskId) => `/pert/task-transaction-api/?parent_task_id=${taskId}&project_id=${shipId}`,
          label: 'Sub-subtasks',
          resourceType: 'task',
          field: 'task'
        };
      case 'Piping':
        return {
          endpoint: (taskId) => `/pert/task-transaction-api/?parent_task_id=${taskId}&project_id=${shipId}`,
          label: 'Systems',
          resourceType: 'system',
          field: 'system'
        };
      case 'Compartment Inspection':
        return {
          endpoint: (taskId) => `/pert/task-transaction-api/?parent_task_id=${taskId}&project_id=${shipId}`,
          label: 'Compartments',
          resourceType: 'compartment',
          field: 'compartment'
        };
      case 'Trunking and Ducting':
        return {
          endpoint: (taskId) => `/pert/task-transaction-api/?parent_task_id=${taskId}&project_id=${shipId}`,
          label: 'Units',
          resourceType: 'mpr_unit',
          field: 'mpr_unit'
        };
      default:
        return {
          endpoint: (taskId) => `/pert/task-transaction-api/?parent_task_id=${taskId}&project_id=${shipId}`,
          label: 'Equipment',
          resourceType: 'equipment',
          field: 'equipment'
        };
    }
  };

  const fetchUnitsForTask = async (task) => {
    try {
      const config = getUnitConfig(activeHeader.name);
      const response = await getRequest(config.endpoint(task.id));
      const units = response.data;

      setTaskUnits(prev => ({
        ...prev,
        [task.id]: units
      }));
      setUnitLabels(prev => ({
        ...prev,
        [task.id]: config.label
      }));

      return units;
    } catch (error) {
      console.error('Error fetching units for task:', error);
      return [];
    }
  };

  const fetchTaskDetails = async (taskId) => {
    try {
      const response = await getRequest(`/pert/task-transaction-api/?id=${taskId}&project_id=${shipId}`);
      if (response.data && response.data.length > 0) {
        const taskData = response.data[0];
        setTaskDetail(taskData);
        setExistingFiles(taskData.files || []);
        const newFormData = {
          status: taskData.is_completed ? "Completed" : "In Progress",
          percentage: taskData.actual_completion_percent || 0,
          startDate: taskData.actual_start ? taskData.actual_start.split('T')[0] : "",
          endDate: taskData.actual_finish ? taskData.actual_finish.split('T')[0] : "",
          remarks: taskData.remarks || "",
          transactionId: taskData.id || null
        };
        setFormData(newFormData);
        setInitialFormData(newFormData);
      } else {
        setFormData({
          status: "In Progress",
          percentage: 0,
          startDate: "",
          endDate: "",
          remarks: "",
          transactionId: null
        });
        setInitialFormData({});
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      setFormData({
        status: "In Progress",
        percentage: 0,
        startDate: "",
        endDate: "",
        remarks: "",
        transactionId: null
      });
      setInitialFormData({});
    }
  };

  const toggleTaskExpansion = async (task) => {
    if (expandedTaskId === task.id) {
      setExpandedTaskId(null);
      setSelectedTask(null);
      setSelectedUnit(null);
      setSearchTerm('');
      setIsEndOfHierarchy(false);
      setTaskDetail(null);
      setUploadedFiles([]);
      setFormData({
        status: "In Progress",
        percentage: 0,
        startDate: "",
        endDate: "",
        remarks: "",
        transactionId: null
      });
      setInitialFormData({});
      return;
    }

    setExpandedTaskId(task.id);
    setSelectedTask(task);
    setSelectedUnit(null);
    setSearchTerm('');
    setUploadedFiles([]);

    const units = await fetchUnitsForTask(task);

    if (units.length === 0) {
      setIsEndOfHierarchy(true);
      setSelectedUnit(task);
      await fetchTaskDetails(task.id);
    } else {
      setIsEndOfHierarchy(false);
      setFormData({
        status: "In Progress",
        percentage: 0,
        startDate: "",
        endDate: "",
        remarks: "",
        transactionId: null
      });
      setInitialFormData({});
    }

    try {
      const transactionsRes = await getRequest(
        `/transaction/task-transaction?project_id=${shipId}&task__parent_task=${task.id}`
      );
      setExistingTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  useEffect(() => {
    if (selectedTask && selectedUnit && !isEndOfHierarchy) {
      const transaction = existingTransactions.find(t =>
        t.task?.id === selectedTask.id &&
        (t.equipment === selectedUnit.id ||
          t.system === selectedUnit.id ||
          t.compartment === selectedUnit.id ||
          t.mpr_unit === selectedUnit.id)
      );

      const newFormData = {
        status: transaction?.is_completed ? "Completed" : "In Progress",
        percentage: transaction?.actual_completion_percent || 0,
        startDate: transaction?.actual_start?.split('T')[0] || "",
        endDate: transaction?.actual_finish?.split('T')[0] || "",
        remarks: transaction?.remarks || "",
        transactionId: transaction?.id || null
      };
      setFormData(newFormData);
      setInitialFormData(newFormData);
    }
  }, [selectedUnit, existingTransactions, selectedTask, isEndOfHierarchy]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setFormData(prev => {
      let newPercentage = prev.percentage;
      if (newStatus === "Completed") {
        newPercentage = 100;
      } else if (newStatus === "In Progress") {
        newPercentage = initialFormData.percentage !== undefined ? initialFormData.percentage : 0;
      }
      return {
        ...prev,
        status: newStatus,
        percentage: newPercentage
      };
    });
  };

  // Simplified file handling without drag-and-drop
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileToRemove) => {
    setUploadedFiles(prev => 
      prev.filter(f => f.file !== fileToRemove.file)
    );
    URL.revokeObjectURL(fileToRemove.preview);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeHeader || !selectedTask) return;

    const config = getUnitConfig(activeHeader.name);
    let payload = {
      id: formData.transactionId || undefined,
      remarks: formData.remarks,
      actual_start: formData.startDate || null,
      actual_finish: formData.endDate || null,
      actual_completion_percent: formData.percentage,
      is_completed: formData.status === "Completed",
      task: selectedTask.id,
      resource_type: config.resourceType,
      project: shipId,
    };

    // Conditionally add the resource field based on the config
    if (config.field) {
      if (config.field === 'system') {
        payload[config.field] = null; // Set system to null by default
      } else {
        payload[config.field] = isEndOfHierarchy
          ? selectedTask.id
          : (selectedUnit?.id || null);
      }
    }

    const formDataWithFiles = new FormData();
    
    for (const key in payload) {
      if (payload[key] !== undefined && payload[key] !== null) {
        formDataWithFiles.append(key, payload[key]);
      }
    }
    
    // This loop correctly appends each file. If uploadedFiles is empty, this loop won't run.
    uploadedFiles.forEach(fileObj => {
      formDataWithFiles.append('uploaded_files', fileObj.file);
    });

    try {
      // Corrected postRequest call: Pass true for the isMultipart parameter
      await postRequest(
        `/pert/task-transaction-api/`,
        formDataWithFiles,
        true // This ensures 'Content-Type' is 'multipart/form-data'
      );

      toast.success("Data saved successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      uploadedFiles.forEach(file => URL.revokeObjectURL(file.preview));
      setUploadedFiles([]);

      if (isEndOfHierarchy) {
        await fetchTaskDetails(selectedTask.id);
      } else if (selectedUnit) {
        const transactionsRes = await getRequest(
          `/transaction/task-transaction?project_id=${shipId}&task=${selectedTask.id}&${config.field}=${selectedUnit.id}`
        );
        setExistingTransactions(transactionsRes.data);
      } else {
        const transactionsRes = await getRequest(
          `/transaction/task-transaction?project_id=${shipId}&task__parent_task=${selectedTask.id}`
        );
        setExistingTransactions(transactionsRes.data);
      }

    } catch (error) {
      console.error('Error saving data:', error);
      toast.error("Error saving data!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onClose();
  };

  const getFileType = (fileName) => {
    if (!fileName) return 'unknown';
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) {
      return 'image';
    } else if (['pdf'].includes(ext)) {
      return 'pdf';
    } else if (['doc', 'docx'].includes(ext)) {
      return 'document';
    } else if (['xls', 'xlsx'].includes(ext)) {
      return 'spreadsheet';
    }
    return 'other';
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 backdrop-blur-sm">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: 9999 }}
      />
      <div
        className={`bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden animate-fadeIn border border-gray-200 ${
          isFullscreen
            ? "fixed inset-0 m-0 rounded-none"
            : "w-full max-w-7xl h-5/6"
        }`}
      >
        <div className="bg-[#1e3a8a] text-white px-6 py-4 flex justify-between items-center shadow-lg">
          <div>
            <h2 className="text-xl font-bold">Ship Inspection Form</h2>
            <div className="text-sm mt-1 opacity-75">
              Comprehensive Task Management System
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg hover:bg-white hover:bg-opacity-20 transition-all duration-200 transform hover:scale-105"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ?
                <Minimize className="w-5 h-5" /> :
                <Maximize className="w-5 h-5" />
              }
            </button>
            <button
              onClick={handleCloseModal}
              className="p-2 rounded-lg hover:bg-red-500 hover:bg-opacity-20 transition-all duration-200 transform hover:scale-105"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="relative px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-400">
            {headers.map((header, index) => {
              const colors = getHeaderColor(index);
              return (
                <button
                  key={header.id}
                  className={`px-6 py-3 whitespace-nowrap rounded-lg transition-all duration-300 transform border-2 shadow-lg ${
                    activeHeader?.id === header.id
                      ? `${colors.bg} ${colors.text} font-semibold scale-105 shadow-xl ${colors.shadow}`
                      : `bg-white text-gray-700 border-gray-200 hover:shadow-lg hover:scale-102 hover:border-gray-300`
                  }`}
                  onClick={() => setActiveHeader(header)}
                >
                  <span className="flex items-center">
                    {header.name}
                    {activeHeader?.id === header.id && (
                      <div className="ml-2 w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-1/3 border-r border-gray-200 bg-gradient-to-b from-gray-50 to-white flex flex-col shadow-inner">
            <div>
              <div
                className={`px-6 py-4 flex justify-between items-center border-b border-gray-200 ${
                  !activeHeader
                    ? "opacity-50 cursor-not-allowed bg-gray-100"
                    : "hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 cursor-pointer transition-all duration-200"
                }`}
                onClick={() => activeHeader && setIsTaskAccordionOpen(!isTaskAccordionOpen)}
              >
                <h3 className="font-semibold text-gray-800 flex items-center">
                  <Clipboard className="w-5 h-5 mr-2 text-blue-600" />
                  Tasks
                </h3>
                {activeHeader ? (
                  <div className={`p-1 rounded-full ${isTaskAccordionOpen ? 'bg-blue-100' : 'bg-gray-100'} transition-all duration-200`}>
                    {isTaskAccordionOpen ?
                      <ChevronUp className="w-5 h-5 text-blue-600" /> :
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    }
                  </div>
                ) : (
                  <span className="text-xs text-gray-500 italic">Select a header first</span>
                )}
              </div>

              {isTaskAccordionOpen && activeHeader && (
                <div className="max-h-[calc(100vh-400px)] overflow-y-auto bg-white animate-slideDown scrollbar-thin scrollbar-thumb-blue-200 p-2">
                  {tasks.map((task, index) => (
                    <div
                      key={task.id}
                      className={`mb-3 rounded-lg transition-all duration-200 overflow-hidden ${
                        selectedTask?.id === task.id
                          ? "border-4 border-blue-600 bg-blue-100 shadow-lg"
                          : "border-2 border-gray-300 bg-gray-100 hover:border-gray-400"
                      }`}
                    >
                      <div
                        className={`px-6 py-4 cursor-pointer flex justify-between items-center ${
                          selectedTask?.id === task.id
                            ? "bg-gradient-to-r from-blue-200 to-indigo-200 text-blue-900 font-semibold"
                            : "hover:bg-gradient-to-r hover:from-gray-100 hover:to-blue-100 text-gray-800"
                        }`}
                        onClick={() => toggleTaskExpansion(task)}
                      >
                        <span className="pl-2">
                          {task.name}
                        </span>
                        <div className={`p-1 rounded-full transition-all duration-200 ${
                          expandedTaskId === task.id ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          {expandedTaskId === task.id ?
                            <ChevronUp className="w-4 h-4 text-blue-600" /> :
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          }
                        </div>
                      </div>

                      {expandedTaskId === task.id && taskUnits[task.id]?.length > 0 && (
                        <div className="bg-gradient-to-r from-gray-50 to-blue-50 pl-8 animate-slideDown border-t border-blue-200">
                          <div className="text-sm font-semibold text-gray-700 py-3 px-4 border-b border-blue-200 flex items-center justify-between bg-gradient-to-r from-blue-100 to-indigo-100">
                            <span className="flex items-center text-blue-700">
                              <Search className="w-4 h-4 mr-2" />
                              {unitLabels[task.id] || "Sub-tasks"}
                            </span>
                            <div className="relative w-48">
                              <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white shadow-sm"
                              />
                              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-200 p-2">
                            {taskUnits[task.id] ? (
                              taskUnits[task.id].filter(unit =>
                                unit.name.toLowerCase().includes(searchTerm.toLowerCase())
                              ).map((unit, unitIndex) => (
                                <div
                                  key={unit.id}
                                  className={`mb-2 last:mb-0 p-3 rounded-md cursor-pointer transition-all duration-200 ${
                                    selectedUnit?.id === unit.id
                                      ? "bg-gradient-to-r from-green-100 to-teal-100 border-4 border-green-600 text-green-900 font-medium shadow-md"
                                      : "bg-purple-50 border-2 border-purple-200 hover:bg-gradient-to-r hover:from-purple-100 hover:to-indigo-100 text-gray-800"
                                  }`}
                                  onClick={() => {
                                    setSelectedUnit(unit);
                                    fetchTaskDetails(unit.id);
                                  }}
                                >
                                  <span className="font-medium">
                                    {unit.name}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="p-6 text-gray-500 text-sm flex justify-center items-center">
                                <div className="animate-pulse flex items-center">
                                  <div className="w-4 h-4 bg-blue-200 rounded-full mr-2 animate-bounce"></div>
                                  Loading...
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="w-2/3 p-8 overflow-y-auto bg-gradient-to-br from-white to-gray-50">
            {selectedTask && (isEndOfHierarchy || selectedUnit) ? (
              <div className="animate-fadeIn">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                  <h3 className="text-2xl font-bold mb-6 text-gray-800 pb-4 border-b border-gray-200 flex items-center">
                    <CheckCircle2 className="w-6 h-6 mr-3 text-green-600" />
                    {activeHeader?.name} - {selectedTask.name}
                    {selectedUnit && selectedUnit.id !== selectedTask.id ? ` - ${selectedUnit.name}` : ''}
                  </h3>

                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                      <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                        <CheckCircle2 className="w-5 h-5 mr-2 text-green-600" />
                        Status
                      </label>
                      <select
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm bg-white"
                        value={formData.status}
                        onChange={handleStatusChange}
                      >
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                      <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                        <Clock3 className="w-5 h-5 mr-2 text-blue-600" />
                        Completion Percentage:
                        <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
                          {formData.percentage.toFixed(1)}%
                        </span>
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={formData.percentage}
                        onChange={(e) => setFormData({...formData, percentage: parseFloat(e.target.value)})}
                        className="w-full h-3 bg-gradient-to-r from-blue-200 to-purple-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                        <span>0%</span>
                        <span>25%</span>
                        <span>50%</span>
                        <span>75%</span>
                        <span>100%</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                        <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                          <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm bg-white"
                        />
                      </div>
                      <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                        <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                          <Calendar className="w-5 h-5 mr-2 text-red-600" />
                          End Date
                        </label>
                        <input
                          type="date"
                          value={formData.endDate}
                          onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm bg-white"
                        />
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                      <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                        <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
                        Remarks
                      </label>
                      <textarea
                        rows="4"
                        value={formData.remarks}
                        onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                        className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none shadow-sm bg-white"
                        placeholder="Add any additional notes or comments here..."
                      />
                    </div>

                    <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg border border-green-200">
                      <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                        <UploadCloud className="w-5 h-5 mr-2 text-green-600" />
                        Upload Files
                      </label>
                      
                      <div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-blue-400 transition-colors duration-200 bg-white">
                        <UploadCloud className="w-12 h-12 text-gray-400 mb-3" />
                        <p className="text-gray-700 font-medium">Click to select files</p>
                        <p className="text-gray-500 text-sm mt-1">or</p>
                        <input 
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload"
                        />
                        <button
                          type="button"
                          className="mt-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
                          onClick={() => document.getElementById('file-upload').click()}
                        >
                          <File className="w-4 h-4 mr-2" />
                          Choose File
                        </button>
                      </div>

                      {uploadedFiles.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-md font-semibold text-gray-700 mb-3">
                            Uploaded Files:
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {uploadedFiles.map((fileObj, index) => {
                              const file = fileObj.file;
                              return (
                                <div key={index} className="relative bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center space-x-3 shadow-sm">
                                  {file.type.startsWith('image/') ? (
                                    <img
                                      src={fileObj.preview}
                                      alt={file.name}
                                      className="h-16 w-16 object-cover rounded-md border border-gray-300"
                                    />
                                  ) : file.type === 'application/pdf' ? (
                                    <div className="h-16 w-16 flex items-center justify-center bg-red-100 text-red-600 rounded-md">
                                      <FileText className="w-8 h-8" />
                                    </div>
                                  ) : (
                                    <div className="h-16 w-16 flex items-center justify-center bg-blue-100 text-blue-600 rounded-md">
                                      <File className="w-8 h-8" />
                                    </div>
                                  )}
                                  <div className="flex-1 overflow-hidden">
                                    <p className="text-sm font-medium text-gray-800 truncate" title={file.name}>
                                      {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeFile(fileObj)}
                                    className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors duration-200"
                                    title="Remove file"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Existing Files Section */}
                    {existingFiles.length > 0 && (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                        <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                          <File className="w-5 h-5 mr-2 text-amber-600" />
                          Existing Files
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {existingFiles.map((file) => {
                            const fileType = getFileType(file.file);
                            const fileName = file.file.split('/').pop();
                            
                            return (
                              <div 
                                key={file.id} 
                                className="relative bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center space-x-3 shadow-sm hover:shadow-md transition-shadow"
                              >
                                <div className="flex-shrink-0">
                                  {fileType === 'image' ? (
                                    <div className="h-16 w-16 flex items-center justify-center bg-blue-100 text-blue-600 rounded-md">
                                      <FileImage className="w-8 h-8" />
                                    </div>
                                  ) : fileType === 'pdf' ? (
                                    <div className="h-16 w-16 flex items-center justify-center bg-red-100 text-red-600 rounded-md">
                                      <FileText className="w-8 h-8" />
                                    </div>
                                  ) : (
                                    <div className="h-16 w-16 flex items-center justify-center bg-gray-100 text-gray-600 rounded-md">
                                      <File className="w-8 h-8" />
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <p 
                                    className="text-sm font-medium text-gray-800 truncate"
                                    title={fileName}
                                  >
                                    {fileName}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(file.created_on).toLocaleDateString()}
                                  </p>
                                </div>
                                
                                <a
                                  href={`https://pms-api.ilizien-projects-cdf.in${file.file}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                                  title="Download file"
                                >
                                  <Download className="w-4 h-4" />
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end pt-4">
                      <button
                        onClick={handleSubmit}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-8 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50 flex items-center shadow-lg font-semibold"
                      >
                        <Save className="w-5 h-5 mr-2" />
                        Save Inspection
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-500 animate-pulse">
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-8 rounded-full mb-6 shadow-lg">
                  <Clipboard className="w-16 h-16 text-blue-400" />
                </div>
                <p className="text-xl font-medium text-gray-600">Select a task to begin</p>
                <p className="text-sm text-gray-400 mt-2">Choose from the options in the left panel</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slideDown {
    from { max-height: 0; opacity: 0; transform: translateY(-10px); }
    to { max-height: 500px; opacity: 1; transform: translateY(0); }
  }

  .animate-fadeIn {
    animation: fadeIn 0.4s ease-out forwards;
  }

  .animate-slideDown {
    animation: slideDown 0.3s ease-out forwards;
  }

  .scrollbar-thin::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }

  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f8fafc;
    border-radius: 10px;
  }

  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: linear-gradient(45deg, #60a5fa, #a78bfa);
    border-radius: 10px;
  }

  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(45deg, #3b82f6, #8b5cf6);
  }

  .scrollbar-thumb-gray-400::-webkit-scrollbar-thumb {
    background: linear-gradient(45deg, #9ca3af, #6b7280);
  }

  .scrollbar-thumb-blue-200::-webkit-scrollbar-thumb {
    background: linear-gradient(45deg, #93c5fd, #ddd6fe);
  }

  .slider::-webkit-slider-thumb {
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: linear-gradient(45deg, #3b82f6, #8b5cf6);
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
    border: 2px solid white;
  }

  .slider::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 12px rgba(59, 130, 246, 0.4);
  }

  .hover\\:scale-102:hover {
    transform: scale(1.02);
  }

  .bg-gradient-status-complete {
    background: linear-gradient(135deg, #10b981, #059669);
  }

  .bg-gradient-status-progress {
    background: linear-gradient(135deg, #f59e0b, #d97706);
  }

  button {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .backdrop-blur-sm {
    backdrop-filter: blur(4px);
  }

  .shadow-xl {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  .shadow-2xl {
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }

  .shadow-purple-200 {
    box-shadow: 0 10px 15px -3px rgba(196, 181, 253, 0.3), 0 4px 6px -2px rgba(196, 181, 253, 0.2);
  }

  .shadow-blue-200 {
    box-shadow: 0 10px 15px -3px rgba(147, 197, 253, 0.3), 0 4px 6px -2px rgba(147, 197, 253, 0.2);
  }

  .shadow-green-200 {
    box-shadow: 0 10px 15px -3px rgba(134, 239, 172, 0.3), 0 4px 6px -2px rgba(134, 239, 172, 0.2);
  }

  .shadow-orange-200 {
    box-shadow: 0 10px 15px -3px rgba(254, 215, 170, 0.3), 0 4px 6px -2px rgba(254, 215, 170, 0.2);
  }

  .shadow-red-200 {
    box-shadow: 0 10px 15px -3px rgba(254, 202, 202, 0.3), 0 4px 6px -2px rgba(254, 202, 202, 0.2);
  }

  .shadow-teal-200 {
    box-shadow: 0 10px 15px -3px rgba(153, 246, 228, 0.3), 0 4px 6px -2px rgba(153, 246, 228, 0.2);
  }

  .shadow-indigo-200 {
    box-shadow: 0 10px 15px -3px rgba(165, 180, 252, 0.3), 0 4px 6px -2px rgba(165, 180, 252, 0.2);
  }

  .shadow-pink-200 {
    box-shadow: 0 10px 15px -3px rgba(251, 207, 232, 0.3), 0 4px 6px -2px rgba(251, 207, 232, 0.2);
  }

  .shadow-cyan-200 {
    box-shadow: 0 10px 15px -3px rgba(165, 243, 252, 0.3), 0 4px 6px -2px rgba(165, 243, 252, 0.2);
  }

  .shadow-yellow-200 {
    box-shadow: 0 10px 15px -3px rgba(254, 240, 138, 0.3), 0 4px 6px -2px rgba(254, 240, 138, 0.2);
  }
`;
document.head.appendChild(style);